import styled from "@emotion/styled";
import { MDSText } from "@/mds/components/text";
import { MDSMenuItemVariant, MDSMenuItemIconVariant } from "./menu-item.types";

const VARIANT_HEIGHT_MAP: Record<MDSMenuItemVariant, string> = {
  [MDSMenuItemVariant.OneLine]: "32px",
  [MDSMenuItemVariant.TwoLine]: "50px",
};

export const StyledWrapper = styled.div<{
  $variant: MDSMenuItemVariant;
  $isHovered: boolean;
  $isInert: boolean;
  $hasIcon: boolean;
  $hasRightAddon: boolean;
}>(
  ({ theme, $variant, $isHovered, $isInert, $hasIcon, $hasRightAddon }) => `
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  border: none;
  border-radius: 0;
  margin: 0;
  display: grid;
  grid-template-columns: ${
    $hasIcon && $hasRightAddon
      ? "auto minmax(0, 1fr) auto"
      : $hasIcon
        ? "auto minmax(0, 1fr)"
        : $hasRightAddon
          ? "minmax(0, 1fr) auto"
          : "minmax(0, 1fr)"
  };
  column-gap: ${theme.spacing[2]};
  flex-shrink: 0;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  cursor: ${$isInert ? "default" : "pointer"};
  background-color: ${
    $isInert
      ? theme.colors.background.menu.default
      : $isHovered
        ? theme.colors.background.menu.hover
        : theme.colors.background.menu.default
  };
  height: ${VARIANT_HEIGHT_MAP[$variant]};

  &[data-focused], [data-focused] & {
    background-color: ${theme.colors.background.menu.hover};
  }

  &[data-selected], [data-selected] & {
    background-color: ${theme.colors.background.menu.selected};
  }

  &:hover:not([data-disabled]), :hover:not([data-disabled]) & {
    background-color: ${theme.colors.background.menu.hover};
  }

  &[data-disabled], [data-disabled] & {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: ${theme.colors.background.menu.default};
  }

  [data-mds-name="action-menu"] &,
  [data-mds-name="select-menu"] & {
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  }

  ${
    $isInert
      ? `
  &[data-focused], [data-focused] &,
  &[data-selected], [data-selected] &,
  &:hover:not([data-disabled]), :hover:not([data-disabled]) & {
    background-color: ${theme.colors.background.menu.default};
  }
`
      : ""
  }
`
);

export const StyledIconWrapper = styled.div<{
  $iconVariant: MDSMenuItemIconVariant;
  $hasSubtitle: boolean;
}>`
  display: flex;
  align-items: ${({ $iconVariant, $hasSubtitle }) =>
    $iconVariant === MDSMenuItemIconVariant.Avatar || !$hasSubtitle ? "center" : "flex-start"};
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  padding-right: ${({ theme, $iconVariant }) =>
    $iconVariant === MDSMenuItemIconVariant.Avatar ? theme.spacing[1] : 0};

  [data-mds-name="action-menu"] &,
  [data-mds-name="select-menu"] & {
    grid-column: 1;
  }
`;

export const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  max-width: 100%;

  [data-mds-name="action-menu"] &,
  [data-mds-name="select-menu"] & {
    grid-column: 2;
  }
`;

export const StyledTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  min-width: 0;
  max-width: 100%;
`;

export const StyledTitle = styled(MDSText)`
  min-width: 0;
  max-width: 100%;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledExtraText = styled(MDSText)`
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StyledSubtitle = styled.div`
  margin-top: ${({ theme }) => theme.spacing[0.5]};
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledRightAddon = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  justify-self: end;
  flex-shrink: 0;
  min-width: max-content;
  align-self: center;
  padding-left: ${({ theme }) => `calc(${theme.spacing[4]} + ${theme.spacing[0.5]})`};

  [data-mds-name="action-menu"] &,
  [data-mds-name="select-menu"] & {
    grid-column: 3;
  }
`;
